import React, { useState } from 'react';
import {
  LocalizationProvider,
  DatePicker
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LoadingButton } from '@mui/lab';
import { Box, TextField, Alert, Stack, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { TextFieldWithPopup } from './TextFieldWithPopup';
import { ExemptionFieldsSection } from './ExemptionFieldsSection';
import { PLAN_TYPES, PLAN_TYPE_LABELS, isExemption } from '@Utils/planType';

// Constants
import {
  FIELD_LABELS,
  ERROR_MESSAGES,
  BUTTON_LABELS,
  ALERT_MESSAGES,
  RESTRICTION_TYPES,
  DEFAULTS,
  TYPE_SWITCH_LABEL
} from './ExitPlanFormSection.constants';

// Utilities
import {
  isDateValid,
  getMinDate,
  isEmpty,
  parseDate,
  getFieldError,
  isDateSet
} from './ExitPlanFormSection.utils';

export const ExitPlanFormSection = ({
                                      // ================ Data Props ================
                                      requestData,
                                      formErrors,

                                      // ================ Date State Props ================
                                      exitDate,
                                      endOfProductionDate,
                                      endOfImportationDate,

                                      // ================ Text State Props ================
                                      replacementPlan,
                                      liquidationPlan,
                                      additionalComments,

                                      // ================ Plan type + exemption props ================
                                      planType = PLAN_TYPES.EXIT_PLAN,
                                      onPlanTypeChange = () => {},
                                      allowTypeSwitch = false,
                                      toImpactEuros = '',
                                      exceptionStartDate = null,
                                      exceptionEndDate = null,
                                      justification = '',
                                      safetyAssessmentReference = '',
                                      productTypes = '',
                                      brands = '',
                                      setToImpactEuros = () => {},
                                      setExceptionStartDate = () => {},
                                      setExceptionEndDate = () => {},
                                      setJustification = () => {},
                                      setSafetyAssessmentReference = () => {},
                                      setProductTypes = () => {},
                                      setBrands = () => {},

                                      // ================ Control Props ================
                                      submitting,
                                      isEditing,
                                      disableSubmit = false,
                                      showDelete = false,
                                      showSupportAlert = false,

                                      // ================ Handler Props ================
                                      handleSubmit,
                                      onDelete = () => {},
                                      setExitDate,
                                      setEndOfProductionDate,
                                      setEndOfImportationDate,
                                      setReplacementPlan,
                                      setLiquidationPlan,
                                      setAdditionalComments,

                                      // ================ Custom Props ================
                                      createButtonLabel = "Create Plan",
                                    }) => {
  // ================ Local State ================
  const [touched, setTouched] = useState({
    exitDate: false,
    endOfProductionDate: false,
    endOfImportationDate: false,
    replacementPlan: false,
    liquidationPlan: false
  });

  // ================ Computed Values ================
  // Safe access of restriction type with fallback
  const restrictionType = requestData?.exitRequest?.restrictionType || DEFAULTS.RESTRICTION_TYPE;

  // Check if at least one date is set for restricted type
  const atLeastOneDateSet = isDateSet(endOfProductionDate) || isDateSet(endOfImportationDate);

  // Check if required fields are empty based on restriction type
  const requiredFieldsEmpty = restrictionType === RESTRICTION_TYPES.BANNED
      ? !atLeastOneDateSet || isEmpty(liquidationPlan)
      : !atLeastOneDateSet || isEmpty(liquidationPlan);

  // Exemption-only required fields (see @Utils/planType.validateExemptionFields)
  const exemptionRequiredEmpty = isExemption(planType)
      && (!isDateSet(exceptionStartDate) || !isDateSet(exceptionEndDate) || isEmpty(justification));

  const submitLabel = isEditing
      ? (isExemption(planType) ? BUTTON_LABELS.UPDATE_EXEMPTION : BUTTON_LABELS.UPDATE_PLAN)
      : (isExemption(planType) ? BUTTON_LABELS.CREATE_EXEMPTION : createButtonLabel);

  // ================ Helper Functions ================
  const handleFieldBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
  };

  const getError = (fieldName, value, label) => {
    // Special handling for date fields - only show error if both are empty
    if (fieldName === 'endOfProductionDate' || fieldName === 'endOfImportationDate') {
      if (!touched[fieldName] && !formErrors[fieldName]) return '';
      
      // Only show error if neither date is set
      if (!isDateSet(endOfProductionDate) && !isDateSet(endOfImportationDate)) {
        return 'At least one date is required';
      }
      
      return formErrors[fieldName] || '';
    }
    
    return getFieldError(touched[fieldName], formErrors[fieldName], value, label);
  };

  // ================ Render Functions ================
  const renderBannedFields = () => (
      <>
        <DatePicker
            label={FIELD_LABELS.EXIT_DATE}
            value={parseDate(exitDate)}
            onChange={setExitDate}
            minDate={getMinDate()}
            shouldDisableDate={(date) => !isDateValid(date)}
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
                error: Boolean(getError('exitDate', exitDate, ERROR_MESSAGES.EXIT_DATE)),
                helperText: getError('exitDate', exitDate, ERROR_MESSAGES.EXIT_DATE),
                onBlur: () => handleFieldBlur('exitDate')
              },
            }}
        />

        <TextFieldWithPopup
            label={FIELD_LABELS.REPLACEMENT_PLAN}
            value={replacementPlan}
            onChange={(e) => setReplacementPlan(e.target.value)}
            onBlur={() => handleFieldBlur('replacementPlan')}
            error={Boolean(getError('replacementPlan', replacementPlan, ERROR_MESSAGES.REPLACEMENT_PLAN))}
            helperText={getError('replacementPlan', replacementPlan, ERROR_MESSAGES.REPLACEMENT_PLAN)}
            required
        />
      </>
  );

  const renderRestrictedFields = () => (
      <>
        <Box sx={styles.datePickerContainer}>
          <DatePicker
              label={FIELD_LABELS.END_OF_PRODUCTION_DATE}
              value={parseDate(endOfProductionDate)}
              onChange={setEndOfProductionDate}
              minDate={getMinDate()}
              shouldDisableDate={(date) => !isDateValid(date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  error: Boolean(getError('endOfProductionDate', endOfProductionDate, ERROR_MESSAGES.END_OF_PRODUCTION_DATE)),
                  helperText: getError('endOfProductionDate', endOfProductionDate, ERROR_MESSAGES.END_OF_PRODUCTION_DATE),
                  onBlur: () => handleFieldBlur('endOfProductionDate')
                },
              }}
          />
          <DatePicker
              label={FIELD_LABELS.END_OF_IMPORTATION_DATE}
              value={parseDate(endOfImportationDate)}
              onChange={setEndOfImportationDate}
              minDate={getMinDate()}
              shouldDisableDate={(date) => !isDateValid(date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  error: Boolean(getError('endOfImportationDate', endOfImportationDate, ERROR_MESSAGES.END_OF_IMPORTATION_DATE)),
                  helperText: getError('endOfImportationDate', endOfImportationDate, ERROR_MESSAGES.END_OF_IMPORTATION_DATE),
                  onBlur: () => handleFieldBlur('endOfImportationDate')
                },
              }}
          />
        </Box>

        <TextFieldWithPopup
            label={FIELD_LABELS.LIQUIDATION_PLAN}
            value={liquidationPlan}
            onChange={(e) => setLiquidationPlan(e.target.value)}
            onBlur={() => handleFieldBlur('liquidationPlan')}
            error={Boolean(getError('liquidationPlan', liquidationPlan, ERROR_MESSAGES.LIQUIDATION_PLAN))}
            helperText={getError('liquidationPlan', liquidationPlan, ERROR_MESSAGES.LIQUIDATION_PLAN)}
            required
        />
      </>
  );

  const renderActionButtons = () => (
      <Box sx={styles.actionContainer}>
        {/* Support Alert */}
        {showSupportAlert && (
            <Alert severity="warning" sx={styles.supportAlert}>
              {ALERT_MESSAGES.SUPPORT_WARNING}
            </Alert>
        )}

        {/* Buttons Container */}
        <Box sx={styles.buttonsContainer}>
          {showDelete && (
              <Button
                  variant="outlined"
                  color="error"
                  onClick={onDelete}
                  sx={styles.deleteButton}
              >
                {BUTTON_LABELS.DELETE_PLAN}
              </Button>
          )}

          <LoadingButton
              variant="contained"
              size="large"
              loading={submitting}
              onClick={handleSubmit}
              sx={styles.submitButton}
              disabled={disableSubmit || requiredFieldsEmpty || exemptionRequiredEmpty}
          >
            {submitLabel}
          </LoadingButton>
        </Box>
      </Box>
  );

  // ================ Main Render ================
  return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack spacing={2} sx={styles.mainStack}>
          {/* Exit Plan / Exemption Request switch */}
          {allowTypeSwitch && (
              <Box sx={styles.typeSwitchRow}>
                <Typography variant="body2" color="textSecondary">{TYPE_SWITCH_LABEL}</Typography>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    color="primary"
                    value={planType}
                    onChange={(_, value) => value && onPlanTypeChange(value)}
                >
                  <ToggleButton value={PLAN_TYPES.EXIT_PLAN}>{PLAN_TYPE_LABELS.EXIT_PLAN}</ToggleButton>
                  <ToggleButton value={PLAN_TYPES.EXEMPTION_REQUEST}>{PLAN_TYPE_LABELS.EXEMPTION_REQUEST}</ToggleButton>
                </ToggleButtonGroup>
              </Box>
          )}

          {/* Conditional Fields Based on Restriction Type */}
          {restrictionType === RESTRICTION_TYPES.BANNED
              ? renderRestrictedFields()
              : renderRestrictedFields()
          }

          {/* Exemption-only fields */}
          {isExemption(planType) && (
              <ExemptionFieldsSection
                  toImpactEuros={toImpactEuros}
                  exceptionStartDate={exceptionStartDate}
                  exceptionEndDate={exceptionEndDate}
                  justification={justification}
                  safetyAssessmentReference={safetyAssessmentReference}
                  productTypes={productTypes}
                  brands={brands}
                  setToImpactEuros={setToImpactEuros}
                  setExceptionStartDate={setExceptionStartDate}
                  setExceptionEndDate={setExceptionEndDate}
                  setJustification={setJustification}
                  setSafetyAssessmentReference={setSafetyAssessmentReference}
                  setProductTypes={setProductTypes}
                  setBrands={setBrands}
                  formErrors={formErrors}
              />
          )}

          {/* Additional Comments Field */}
          <TextField
              label={FIELD_LABELS.ADDITIONAL_COMMENTS}
              multiline
              rows={DEFAULTS.TEXT_FIELD_ROWS}
              fullWidth
              value={additionalComments}
              onChange={(e) => setAdditionalComments(e.target.value)}
          />

          {/* Action Buttons Section */}
          {renderActionButtons()}
        </Stack>
      </LocalizationProvider>
  );
};

// ================ Component Styles ================
const styles = {
  mainStack: {
    mt: 1
  },
  datePickerContainer: {
    display: 'flex',
    gap: 2
  },
  typeSwitchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 2
  },
  actionContainer: {
    mt: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 2
  },
  supportAlert: {
    flex: 1
  },
  buttonsContainer: {
    display: 'flex',
    gap: 1,
    ml: 'auto'
  },
  deleteButton: {
    minWidth: 120,
    fontWeight: 500
  },
  submitButton: {
    minWidth: 200,
    ml: 2,
    boxShadow: 2,
    flexShrink: 0,
    '&:hover': {
      boxShadow: 3,
      transform: 'translateY(-1px)'
    }
  }
};

// ================ Export Component ================
export default ExitPlanFormSection;